/*
 * Promotes the product accordions into a tab strip.
 *
 * Implements the WAI-ARIA tabs pattern with automatic activation: only the selected tab is in
 * the page tab order, and Left/Right/Home/End move between them, which is what a `role="tablist"`
 * promises a screen reader and keyboard user. Automatic activation (arrow both moves focus and
 * selects) is appropriate here because the panels are already in the DOM -- nothing is fetched
 * on selection, so there is no cost to selecting as you arrow past.
 */
class ProductTabs {
  static init() {
    document.querySelectorAll('[data-product-tabs]').forEach((host) => new ProductTabs(host).build());
  }

  constructor(host) {
    this.host = host;
  }

  build() {
    this.accordions = [...this.host.querySelectorAll('.product__accordion.quick-add-hidden')];
    if (this.accordions.length === 0) return;

    const tabs = document.createElement('div');
    tabs.className = 'product-tabs';

    const bar = document.createElement('div');
    bar.className = 'product-tabs__bar';
    bar.setAttribute('role', 'tablist');

    this.buttons = [];
    this.panels = [];

    this.accordions.forEach((accordion, index) => {
      const titleEl = accordion.querySelector('.accordion__title');
      const title = (titleEl && titleEl.textContent.trim()) || `Tab ${index + 1}`;
      const content = accordion.querySelector('.accordion__content');
      const isFirst = index === 0;

      const button = document.createElement('button');
      button.type = 'button';
      button.id = `product-tabs-button-${index}`;
      button.className = 'product-tabs__button';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `product-tabs-panel-${index}`);
      button.setAttribute('aria-selected', String(isFirst));
      // Roving tabindex: one stop for the whole strip, then arrows from there.
      button.tabIndex = isFirst ? 0 : -1;
      if (isFirst) button.classList.add('is-active');
      button.textContent = title;
      button.addEventListener('click', () => this.activate(index));
      bar.appendChild(button);
      this.buttons.push(button);

      const panel = document.createElement('div');
      panel.className = 'product-tabs__panel';
      panel.id = `product-tabs-panel-${index}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `product-tabs-button-${index}`);
      // Focusable so keyboard users can reach panel prose that holds no control of its own.
      panel.tabIndex = 0;
      if (content) {
        // Move the nodes rather than copy innerHTML. Stringifying drops event listeners and
        // component state, which silently breaks any app-injected widget living in a Specs or
        // Sizing block. `childNodes` is live, so spread it to snapshot before appending.
        panel.append(...content.childNodes);
      }
      if (isFirst) panel.classList.add('is-active');
      tabs.appendChild(panel);
      this.panels.push(panel);
    });

    bar.addEventListener('keydown', (event) => this.onKeydown(event));

    tabs.insertBefore(bar, tabs.firstChild);
    this.accordions[0].insertAdjacentElement('beforebegin', tabs);
    // Safe after the move above: the content nodes now live in the panels, not the accordions.
    this.accordions.forEach((accordion) => accordion.remove());
  }

  onKeydown(event) {
    const current = this.buttons.indexOf(document.activeElement);
    if (current === -1) return;

    const last = this.buttons.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next);
    this.buttons[next].focus();
  }

  activate(index) {
    this.buttons.forEach((button, i) => {
      const selected = i === index;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    this.panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ProductTabs.init(), { once: true });
} else {
  ProductTabs.init();
}
